import React from 'react'
import { Icon, Tooltip, Spin, Button } from 'antd'
import { T, speakerName, speakerColor, fmtMs } from './SharedLayout'
import InlineAudioPlayer from './InlineAudioPlayer'

/**
 * TimelineSegmentRow
 *
 * Renders an individual segment in the list.
 * Highlights if playheadMs is within segment range.
 */
const TimelineSegmentRow = ({
  segment,
  index,
  isLast,
  playheadMs,
  isPlaying,
  isLoading,
  pendingText,
  hasPending,
  hasError,
  characterMap,
  onEdit
}) => {
  const isPause = segment.type === 'pause'
  const color = speakerColor(segment.speaker)

  // Active Highlighting Logic
  const isActive =
    isPlaying && playheadMs >= segment.start_ms && playheadMs < segment.end_ms

  const activeStyle = isActive
    ? {
        background: isPause ? T.borderLight : `${color}10`, // 10% opacity
        borderLeft: `5px solid ${isPause ? T.inkLight : color}`,
        boxShadow: `inset 0 0 10px ${isPause ? 'transparent' : `${color}15`}`
      }
    : {}

  return (
    <div
      className={`segment-row ${isActive ? 'active' : ''}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: isPause ? '8px 16px' : '10px 16px',
        background: isPause ? T.surface : T.bg,
        borderBottom: !isLast ? `1px solid ${T.borderLight}` : 'none',
        borderLeft: `3px solid ${isPause ? T.border : color}`,
        transition: 'all 0.2s ease',
        position: 'relative',
        ...activeStyle
      }}
    >
      {isPause ? (
        <>
          <Icon
            type="clock-circle"
            style={{ fontSize: 12, color: T.inkLight, flexShrink: 0 }}
          />
          <span
            style={{
              fontSize: 12,
              color: T.inkLight,
              flex: 1,
              fontWeight: isActive ? 600 : 400
            }}
          >
            Silence / Pause
          </span>
          <span
            style={{
              fontSize: 11,
              color: T.inkLight,
              fontVariantNumeric: 'tabular-nums'
            }}
          >
            {segment.duration_ms}ms
          </span>
        </>
      ) : (
        <>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color,
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              whiteSpace: 'nowrap',
              minWidth: 64,
              flexShrink: 0
            }}
          >
            {speakerName(segment.speaker, characterMap)}
          </div>
          <div
            style={{
              fontSize: 13,
              color: T.ink,
              lineHeight: 1.5,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: isActive ? 500 : 400
            }}
          >
            {pendingText || segment.text}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexShrink: 0
            }}
          >
            {hasPending && (
              <Tooltip title="Text changed, needs regeneration">
                <Icon
                  type="warning"
                  style={{ fontSize: 13, color: T.orange }}
                />
              </Tooltip>
            )}
            {hasError && (
              <Tooltip title="Generation failed">
                <Icon
                  type="close-circle"
                  style={{ fontSize: 13, color: T.red }}
                />
              </Tooltip>
            )}
            {isLoading && <Spin size="small" />}
            <span
              style={{
                fontSize: 11,
                color: T.inkLight,
                fontVariantNumeric: 'tabular-nums',
                minWidth: 84,
                textAlign: 'right'
              }}
            >
              {fmtMs(segment.start_ms)} – {fmtMs(segment.end_ms)}
            </span>
            {segment.audio_url && !isLoading && (
              <div
                style={{ width: 100, flexShrink: 0 }}
                onClick={e => e.stopPropagation()}
              >
                <InlineAudioPlayer
                  src={segment.audio_url}
                  accent={color}
                  compact
                />
              </div>
            )}
          </div>
        </>
      )}

      {/* Edit Trigger */}
      <Tooltip title="Edit this segment">
        <Button
          size="small"
          shape="circle"
          icon="edit"
          onClick={() => onEdit && onEdit(index)}
          style={{
            marginLeft: 4,
            color: T.inkSub,
            borderColor: 'transparent',
            background: isActive ? 'rgba(0,0,0,0.05)' : 'transparent',
            transition: 'all 0.2s'
          }}
        />
      </Tooltip>
    </div>
  )
}

export default TimelineSegmentRow
